import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Group } from '@/types';
import { cn } from '@/lib/utils';

interface OverflowGroupsPopoverProps {
  groups: Group[];
  overflowCount: number;
  className?: string;
}

export function OverflowGroupsPopover({
  groups,
  overflowCount,
  className,
}: OverflowGroupsPopoverProps) {
  const [open, setOpen] = useState(false);

  // Sort groups alphabetically
  const sortedGroups = [...groups].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "px-3 py-1.5 rounded-md border transition-all",
            "text-sm font-medium",
            "bg-muted border-border",
            "hover:bg-muted/80 hover:border-muted-foreground/30",
            "flex items-center gap-1",
            open && "bg-muted/80 border-muted-foreground/30",
            className
          )}
        >
          <span className="text-muted-foreground">+{overflowCount}</span>
          <ChevronDown
            className={cn(
              "h-3 w-3 text-muted-foreground transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[200px] max-w-[500px] p-0"
        align="start"
        sideOffset={8}
      >
        <div className="p-4">
          {/* Header */}
          <div className="pb-3 mb-3 border-b border-[#8FB560]/30">
            <span className="text-xs font-semibold text-[#8FB560] uppercase tracking-wider">
              {groups.length} Groups Selected
            </span>
          </div>

          {/* Alphabetical list with bullet points - responsive columns */}
          <div className="max-h-[300px] overflow-y-auto">
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-x-6">
              {sortedGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-start gap-1.5 py-1 break-inside-avoid"
                >
                  <span className="text-[#8FB560] text-xs mt-1 shrink-0">•</span>
                  <span className="text-sm font-medium text-foreground leading-tight">{group.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
